import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { formatApiErrorDetail } from "../lib/api";
import { landingPathForRole } from "../lib/permissions";
import { Loader2, Lock, ShieldAlert } from "lucide-react";
import KittLogo from "../components/KittLogo";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [errorKind, setErrorKind] = useState("invalid"); // "invalid" | "locked" | "pending" | "suspended"
    const [submitting, setSubmitting] = useState(false);

    if (user && user !== false) return <Navigate to={landingPathForRole(user.role)} replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setErrorKind("invalid");
        setSubmitting(true);
        try {
            const u = await login(email, password);
            navigate(landingPathForRole(u?.role));
        } catch (err) {
            const status = err?.response?.status;
            const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
            // Map specific failure modes to distinct UI states.
            if (status === 423) {
                setErrorKind("locked");
            } else if (status === 403 && /(pending|awaiting|approval)/i.test(detail)) {
                setErrorKind("pending");
            } else if (status === 403 && /suspend/i.test(detail)) {
                setErrorKind("suspended");
            } else {
                setErrorKind("invalid");
            }
            setError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    const errorVisuals = {
        locked: {
            border: "border-red-500/60",
            bg: "bg-red-500/10",
            text: "text-red-300",
            label: "Account Locked",
            testId: "login-error-locked",
        },
        pending: {
            border: "border-signal/60",
            bg: "bg-signal/10",
            text: "text-signal",
            label: "Pending Admin Approval",
            testId: "login-error-pending",
        },
        suspended: {
            border: "border-red-500/60",
            bg: "bg-red-500/10",
            text: "text-red-300",
            label: "Account Suspended",
            testId: "login-error-suspended",
        },
        invalid: {
            border: "border-red-500/50",
            bg: "bg-red-500/5",
            text: "text-red-400",
            label: "Authorization Failed",
            testId: "login-error",
        },
    };
    const ev = errorVisuals[errorKind] || errorVisuals.invalid;

    return (
        <div className="min-h-screen bg-jet text-snow flex">
            {/* Left — branding panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-hairline">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,106,0,0.08),transparent_50%)]" />
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage:
                            "url('https://images.unsplash.com/photo-1775031478823-e415206e4d58?crop=entropy&cs=srgb&fm=jpg&w=1600')",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                {/* scan line */}
                <div className="absolute inset-x-0 h-px bg-signal/40 animate-scan" />

                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div className="flex items-center">
                        <KittLogo size={120} withGlow testId="login-kitt-logo" />
                    </div>

                    <div className="space-y-6">
                        <div className="overline">Mission Brief</div>
                        <h1 className="heading-display text-4xl xl:text-5xl leading-[1.05]">
                            Mission Systems.<br />
                            <span className="text-signal">Mission Ready.</span>
                        </h1>
                        <p className="text-snow-dim text-sm max-w-md leading-relaxed">
                            Built for operators who support the mission.
                        </p>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-xs">
                        <div className="border-l-2 border-signal pl-3">
                            <div className="overline">Gates</div>
                            <div className="heading-display text-2xl mt-1">11</div>
                        </div>
                        <div className="border-l-2 border-hairline pl-3">
                            <div className="overline">Modes</div>
                            <div className="heading-display text-2xl mt-1">03</div>
                        </div>
                        <div className="border-l-2 border-hairline pl-3">
                            <div className="overline">Transfers</div>
                            <div className="heading-display text-2xl mt-1">∞</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right — form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="overline mb-2">Restricted Access</div>
                    <h2 className="heading-display text-3xl mb-1">Operator Sign-In</h2>
                    <p className="text-snow-dim text-sm mb-8">
                        Authenticate to access the KITT terminal.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="overline block mb-2">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-jet-elev1 border border-hairline px-3 py-3 text-snow font-mono text-sm focus:border-signal outline-none transition-colors"
                                placeholder="operator@mission-hq.com"
                            />
                        </div>

                        <div>
                            <label className="overline block mb-2">Password</label>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-jet-elev1 border border-hairline px-3 py-3 text-snow font-mono text-sm focus:border-signal outline-none transition-colors"
                                placeholder="••••••••"
                            />
                        </div>

                        {error && (
                            <div
                                data-testid={ev.testId}
                                className={`border ${ev.border} ${ev.bg} ${ev.text} px-3 py-2.5 text-xs font-mono flex items-start gap-2`}
                            >
                                <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <div className="space-y-0.5">
                                    <div className="font-bold uppercase tracking-wider text-[10px]">{ev.label}</div>
                                    <div>{error}</div>
                                </div>
                            </div>
                        )}

                        <button
                            data-testid="login-submit-btn"
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-signal text-jet hover:bg-signal-hover px-6 py-3 transition-colors font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Lock className="w-4 h-4" />
                            )}
                            Authorize
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
